import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  PagingState,
  CustomPaging,
  FilteringState,
} from '@devexpress/dx-react-grid'
import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableFilterRow,
  PagingPanel,
  TableColumnVisibility,
} from '@devexpress/dx-react-grid-bootstrap4'
import { isEmpty, includes } from 'lodash'
import {
  Row,
  Col,
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import FilterCellComponent from './FilterCell'

const CustomHeaderCell = props => {
  return (
    <TableHeaderRow.Cell
      {...props}
      style={{ color: '#495057', fontWeight: '600', fontSize: '13px' }}
    />
  )
}

CustomHeaderCell.propTypes = {
  showSortingControls: PropTypes.bool,
  sortingEnabled: PropTypes.bool,
  column: PropTypes.object,
  sortingDirection: PropTypes.string,
  onSort: PropTypes.func,
}

const pagingPanelMessages = {
  showAll: 'Todos',
  rowsPerPage: 'Items por página',
  info: '{from} - {to} de {count}',
}

const NoFilterCell = ({ value, ...restProps }) => (
  <VirtualTable.Cell
    {...restProps}
    style={{
      justifyContent: 'flex-end',
    }}
  >
    {value}
  </VirtualTable.Cell>
)

NoFilterCell.propTypes = {
  value: PropTypes.any,
}

const FilterCell = props => {
  const { column } = props
  if (column.disableFilter) {
    return <NoFilterCell {...props} />
  }
  return <FilterCellComponent {...props} />
}

FilterCell.propTypes = {
  column: PropTypes.object,
}

const GeneralList = ({
  columns,
  rows,
  hiddenColumnNames,
  columnExtensions,
  heightOfGrid,
  hideTableHeader,
  showSortingControls,
  withRightAction,
  rightAction,
  showCreateButton,
  createButtonText,
  onClickCreateButton,
  totalCount,
  limit,
  onPageChanged,
  optionsMenu,
  onMenuItemSelected,
  showFilterRow,
  onFiltersChange,
}) => {
  const [currentPage, setCurrentPage] = useState(0)

  const handlePageChange = newPage => {
    setCurrentPage(newPage)
    onPageChanged(newPage)
  }

  const getRowId = row => row.id

  const CellBase = props => {
    if (props.column.name === 'menu') {
      return <VirtualTable.Cell className="general-list-cell general-list-cell-menu" {...props} />
    }
    return <VirtualTable.Cell className="general-list-cell" {...props} />
  }

  const CustomTableRowBase = props => {
    const { row, ...restProps } = props
    const { onRowClicked } = row
    if (!onRowClicked) {
      return <VirtualTable.Row {...restProps} />
    }
    return (
      <VirtualTable.Row
        className="general-list-custom-row"
        onClick={() => (onRowClicked ? onRowClicked(row) : {})}
        {...restProps}
      />
    )
  }

  const handleOnMenuOptionSelected = (itemIndex, optionIndex) => {
    onMenuItemSelected(itemIndex, optionIndex)
  }

  const handleFilterChange = filter => {
    onFiltersChange(filter)
    handlePageChange(0)
  }

  const rowsWithMenu = rows.map((row, rowIndex) => {
    const newRow = row
    newRow.menu = (
      <>
        {withRightAction && rightAction === 'menu' && (
          <UncontrolledDropdown className="general-list-item-menu d-inline-block">
            <DropdownToggle href="#" className="card-drop cursor-pointer" tag="i">
              <i className="mdi mdi-dots-horizontal font-size-18"></i>
            </DropdownToggle>
            <DropdownMenu right>
              {optionsMenu.map((option, optionIndex) => {
                if (!isEmpty(option.fieldCondition)) {
                  if (includes(option.valueCondition, row[option.fieldCondition])) {
                    return (
                      <div key={optionIndex}>
                        <DropdownItem onClick={() => handleOnMenuOptionSelected(rowIndex, optionIndex)}>
                          <i className={`mdi ${option.icon} font-size-16 mr-2`}></i> {option.name}
                        </DropdownItem>
                      </div>
                    )
                  } else {
                    return null
                  }
                }
                return (
                  <div key={optionIndex}>
                    <DropdownItem onClick={() => handleOnMenuOptionSelected(rowIndex, optionIndex)}>
                      <i className={`mdi ${option.icon} font-size-16 mr-2`}></i> {option.name}
                    </DropdownItem>
                  </div>
                )
              })}
            </DropdownMenu>
          </UncontrolledDropdown>
        )}
      </>
    )
    return newRow
  })
  
  return (
    <Card>
      <CardBody>
        { showCreateButton && (
          <Row className="mb-2">
            <Col sm="12">
              <div className="text-sm-right">
                <div onClick={onClickCreateButton} className="btn btn-success btn-rounded waves-effect waves-light mb-2 mr-2">
                  <i className="mdi mdi-plus mr-1"></i> {createButtonText}
                </div>
              </div>
            </Col>
          </Row>
        )}
        <Grid
          rows={rowsWithMenu}
          columns={columns}
          getRowId={getRowId}>
            <VirtualTable
              rowComponent={CustomTableRowBase}
              cellComponent={CellBase}
              width="auto"
              columnExtensions={columnExtensions}
              height={heightOfGrid}
              messages={{ noData: '' }}
            />
            {!hideTableHeader && (
              <TableHeaderRow
                showSortingControls={showSortingControls}
                cellComponent={CustomHeaderCell}
              />
            )}
            <TableColumnVisibility defaultHiddenColumnNames={hiddenColumnNames} />
            <PagingState
              onCurrentPageChange={handlePageChange}
              currentPage={currentPage}
              defaultCurrentPage={0}
              pageSize={limit}
            />
            <CustomPaging totalCount={totalCount} />
            <PagingPanel messages={pagingPanelMessages} />
            <FilteringState
              onFiltersChange={handleFilterChange}
            />
            {showFilterRow && <TableFilterRow cellComponent={FilterCell} />}
        </Grid>
      </CardBody>
    </Card>
  );
}

GeneralList.propTypes = {
  title: PropTypes.string,
  searcherPlaceholder: PropTypes.string,
  columns: PropTypes.array,
  rows: PropTypes.array,
  withRightAction: PropTypes.bool,
  rightAction: PropTypes.string,
  showCreateButton: PropTypes.bool,
  createButtonText: PropTypes.string,
  onClickCreateButton: PropTypes.func,
  optionsMenu: PropTypes.array,
  hiddenColumnNames: PropTypes.array,
  onMenuItemSelected: PropTypes.func,
  onSearcherChange: PropTypes.func,
  onPageChanged: PropTypes.func,
  limit: PropTypes.number,
  heightOfGrid: PropTypes.number,
  totalCount: PropTypes.number,
  hideSearcher: PropTypes.bool,
  showFilterRow: PropTypes.bool,
  onFiltersChange: PropTypes.func,
  sortingData: PropTypes.object,
  showSortingControls: PropTypes.bool,
  rightButtonPress: PropTypes.func,
  rightButtonText: PropTypes.string,
  hideTableHeader: PropTypes.bool,
  blockOption: PropTypes.func,
  columnExtensions: PropTypes.array,
}

GeneralList.defaultProps = {
  title: '',
  searcherPlaceholder: 'Buscar...',
  columns: [],
  rows: [],
  withRightAction: true,
  rightAction: 'menu',
  showCreateButton: false,
  createButtonText: '',
  optionsMenu: [],
  hiddenColumnNames: [],
  onMenuItemSelected: () => {},
  onSearcherChange: () => {},
  onClickCreateButton: () => {},
  onPageChanged: () => {},
  onFiltersChange: () => {},
  rightButtonPress: () => {},
  limit: 10,
  heightOfGrid: 640,
  totalCount: 0,
  hideSearcher: false,
  showFilterRow: false,
  showSortingControls: false,
  hideTableHeader: false,
  blockOption: () => {},
  columnExtensions: [],
}

export default GeneralList
